import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { PlusCircle, LogIn, LayoutDashboard, CreditCard, Calculator, Receipt, User, Wallet } from 'lucide-react';

import logo from './../assets/logo-horizontal.svg'

const Header = ({ isAuthenticated, userInfo }) => {

  const isTemporary = userInfo?.isTemporary || false;

  const NavItem = ({ to, icon, children, variant = 'default' }) => {
    const baseStyles = "flex items-center font-medium rounded-md transition-all duration-200 whitespace-nowrap";
    
    const styles = {
      default: {
        active: "bg-blue-100 text-blue-700 px-4 py-2 text-sm",
        inactive: "text-gray-600 hover:bg-gray-100 hover:text-gray-900 px-4 py-2 text-sm"
      },
      primary: {
        active: "bg-green-100 text-green-800 px-4 py-2 text-sm",
        inactive: "text-green-700 hover:bg-green-50 hover:text-green-800 px-4 py-2 text-sm"
      },
      highlight: {
        active: "bg-blue-700 text-white shadow-md ring-2 ring-blue-300 px-5 py-2.5 text-base rounded-lg",
        inactive: "bg-blue-600 text-white hover:bg-blue-700 shadow hover:shadow-md transform hover:-translate-y-0.5 px-5 py-2.5 text-base rounded-lg"
      }
    };

    const activeClass = styles[variant]?.active || styles.default.active;
    const inactiveClass = styles[variant]?.inactive || styles.default.inactive;

    return (
      <NavLink
        to={to}
        className={({ isActive }) => `${baseStyles} ${isActive ? activeClass : inactiveClass}`}
      >
        {icon}
        <span className="ml-2">{children}</span>
      </NavLink>
    );
  };

  const showAuthedItems = isAuthenticated && !isTemporary;

  // Reusable nav item groups
  const leftItems = (
    <>
      <Link
          to="/"
          className="flex items-center text-blue-600 focus:outline-none rounded-lg p-1 hover:opacity-80 transition-opacity"
       >
          <img 
            src={logo} 
            alt="Recodeq Logo" 
            className="w-40 object-contain" 
          />
      </Link>
      {showAuthedItems && (
        <>
          <NavItem to="/dashboard" icon={<LayoutDashboard size={18} />}>Vaše analýzy</NavItem>
          <NavItem to="/payments/history" icon={<Receipt size={18} />}>Historie transakcí</NavItem>
        </>
      )}
    </>
  );

  const novaAnalyzaButton = showAuthedItems && (
    <NavItem 
      variant="highlight" 
      to="/analysis/create" 
      icon={<PlusCircle size={20} />}
    >
      Nová analýza
    </NavItem>
  );

  const rightItems = showAuthedItems ? (
    <>
      <NavItem to="/payments/calculator" icon={<Calculator size={18} />}>Ceník</NavItem>
      <NavItem to="/payments/buy" icon={<CreditCard size={18} />}>Dobít kredit</NavItem>

      <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 ml-1">
          <div className="flex items-center text-gray-900 font-bold text-sm whitespace-nowrap">
              <Wallet size={14} className="mr-1.5 text-gray-400"/>
              {userInfo?.balance?.toLocaleString() ?? 0} kreditů
          </div>
      </div>

      <NavItem to="/account" icon={<User size={18} />}>Můj účet</NavItem>
    </>
  ) : (
    <NavItem to="/login" icon={<LogIn size={20} />}>Přihlásit se</NavItem>
  );

  return (
    <header className="bg-white/90 backdrop-blur-lg shadow-md fixed top-0 left-0 right-0 z-20 py-3">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* === WIDE VIEW (xl+): CSS Grid with 3 columns. Nová analýza is in the center column → truly centered. === */}
        <div className="hidden xl:grid grid-cols-[1fr_auto_1fr] items-center gap-x-4">
          <div className="flex items-center justify-start gap-x-2">
            {leftItems}
          </div>
          <div className="flex items-center justify-center">
            {novaAnalyzaButton}
          </div>
          <div className="flex items-center justify-end gap-x-2">
            {rightItems}
          </div>
        </div>

        {/* === NARROW VIEW (below xl): Two centered rows. Each row uses flex-wrap to break onto more rows when even narrower. === */}
        <div className="xl:hidden flex flex-col gap-y-3">
          {/* Row 1: logo + nav + Nová analýza */}
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
            {leftItems}
            {novaAnalyzaButton}
          </div>
          {/* Row 2: tools + profile + logout */}
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
            {rightItems}
          </div>
        </div>

      </nav>
    </header>
  );
};

export default Header;